import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { withRouter } from 'react-router-dom';
import OktaSignIn from '@okta/okta-signin-widget';
import '@okta/okta-signin-widget/dist/css/okta-sign-in.min.css';
import logo from '../../client/images/GH_GoldenAdvantage_Logo_RGB.png';
import { promiseResolver } from '../utilities/CommonApiUtils';
import { getUserTypeFromUrl } from '../utilities/UtilityFunctions';
import OktaError from './OktaError';
import {
  OKTA_IMPLICIT_CALLBACK,
  // APP_GROWER_LOGIN,
  APP_SEED_ADVISOR_LOGIN,
  APP_SALES_REPRESENTATIVE_LOGIN,
  forgotPasswordUrl,
} from '../constants/RouteConstants';
import { USER_TYPES } from '../constants/ApplicationConstants';
import {
  getEnvironmentValue,
  OKTA_AUTH_SERVER,
  OKTA_CLIENT_ID,
  OKTA_DOMAIN,
} from '../../envConfig';
import {
  hasCheckedSsoSession,
  beginSsoRedirectCheck,
  cancelSsoRedirectCheck,
} from './ssoRedirect';
import { rememberLoginAttempt } from './loginAccessDenied';

class OktaSignInWidget extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showErrorPopup: false,
    };
    this.userType = getUserTypeFromUrl(USER_TYPES.SEED_ADVISOR);
    this.showForgotPasswordPage = this.showForgotPasswordPage.bind(this);
    this.openEmail = this.openEmail.bind(this);
    this.checkSsoSessionWithRedirect =
      this.checkSsoSessionWithRedirect.bind(this);
  }
  async componentDidMount() {
    let oktaSigninObject = {
      logo: logo,
      baseUrl: this.props.baseUrl,
      authParams: {
        pkce: true,
        issuer: `${getEnvironmentValue(
          OKTA_DOMAIN
        )}/oauth2/${getEnvironmentValue(OKTA_AUTH_SERVER)}`,
        clientId: getEnvironmentValue(OKTA_CLIENT_ID),
        redirectUri: `${window.location.origin}${OKTA_IMPLICIT_CALLBACK}`,
      },
      i18n: {
        en: {
          // Labels
          'primaryauth.title': 'Sign in to Golden Advantage',
          'primaryauth.username.placeholder': 'Email',
          'primaryauth.username.tooltip': 'Enter your email',
          'primaryauth.password.placeholder': 'Password',
          'primaryauth.password.tooltip': 'Enter your password',
          // Errors
          'error.username.required': 'Please enter an email',
          'error.password.required': 'Please enter a password',
          'errors.E0000004': 'Sign in failed!',
          'registration.signup.label': 'New User?',
        },
      },

      customButtons: [
        {
          title: 'Forgot password',
          className: 'btn-customAuth',
          click: this.showForgotPasswordPage,
        },
      ],
      features: {
        registration: false,
        rememberMe: false,
        showPasswordToggleOnSignInPage: true,
        idpDiscovery: true,
      },
      idpDiscovery: {
        requestContext:
          this.userType === USER_TYPES.GROWER
            ? `${window.location.origin}${APP_SEED_ADVISOR_LOGIN}`
            : this.userType === USER_TYPES.SALES_REPRESENTATIVE
            ? `${window.location.origin}${APP_SALES_REPRESENTATIVE_LOGIN}`
            : `${window.location.origin}${APP_SEED_ADVISOR_LOGIN}:`,
      },
    };

    oktaSigninObject.customButtons.push({
      title: 'Have Questions? ',
      className: 'button-as-text',
      click: () => {},
    });
    oktaSigninObject.customButtons.push({
      title:
        'Please contact Golden Advantage Support by phone at (855) 939-0058 or by email at ',
      className: 'contact-text',
      click: () => {},
    });
    oktaSigninObject.customButtons.push({
      title: 'support@goldenadvantage.com',
      className: 'email-link',
      click: this.openEmail,
    });

    const el = ReactDOM.findDOMNode(this);
    this.widget = new OktaSignIn(oktaSigninObject);
    const oktaWidget = this.widget;

    const [sessionError, session] = await promiseResolver(
      oktaWidget.authClient.session.get()
    );

    if (!sessionError && session && session.status === 'ACTIVE') {
      // The Okta callback does not say who signed in, keep it in case it fails.
      rememberLoginAttempt(session.login);
      const [error, res] = await promiseResolver(
        oktaWidget.authClient.token.getWithoutPrompt({
          responseType: ['token', 'id_token'],
          scopes: ['openid', 'email', 'profile'],
        })
      );

      if (!error) {
        oktaWidget.authClient.tokenManager.add('idToken', res.tokens.idToken);
        oktaWidget.authClient.tokenManager.add(
          'accessToken',
          res.tokens.accessToken
        );
        this.props.oktaAuth.authStateManager.updateAuthState();
        return;
      }

      // The session is there but the hidden iframe could not use it, which is what
      // happens with blocked third party cookies. Ask again with a top level redirect
      // before treating it as a real failure.
      if (await this.checkSsoSessionWithRedirect()) {
        return;
      }
      this.setState({ showErrorPopup: true, error });
      return;
    }

    if (window.location.search.includes('fromLogin=true')) {
      await promiseResolver(
        oktaWidget.authClient.token.getWithRedirect({
          responseType: ['token', 'id_token'],
          scopes: ['openid', 'email', 'profile'],
        })
      );
      return;
    }

    // `session.get()` is a cross site call, so an INACTIVE answer may only mean the
    // Okta cookie was not sent with it. Ask Okta in a top level redirect, where the
    // cookie is first party, before falling back to the sign in form.
    if (await this.checkSsoSessionWithRedirect()) {
      return;
    }

    this.widget.renderEl({ el }, this.props.onSuccess, this.props.onError);
  }

  /**
   * Asks Okta for an existing SSO session with a top level redirect to /authorize
   * using `prompt=none`, the only check that still works when third party cookies are
   * blocked. Resolves true when the browser is navigating away, false when the caller
   * should render the sign in widget instead.
   */
  async checkSsoSessionWithRedirect() {
    if (hasCheckedSsoSession()) {
      return false;
    }

    beginSsoRedirectCheck();
    const authClient = this.props.oktaAuth || this.widget.authClient;
    const [error] = await promiseResolver(
      authClient.token.getWithRedirect({
        responseType: ['token', 'id_token'],
        scopes: ['openid', 'email', 'profile'],
        prompt: 'none',
      })
    );

    if (error) {
      cancelSsoRedirectCheck();
      return false;
    }
    return true;
  }

  showForgotPasswordPage() {
    // The type only picks the page copy / return link; the reset itself
    // does not depend on it.
    this.props.history.push(
      forgotPasswordUrl(
        this.userType === USER_TYPES.GROWER ? 'grower' : 'seed-advisor'
      )
    );
  }

  openEmail() {
    window.open('mailto:support@goldenadvantage.com');
  }

  componentWillUnmount() {
    this.widget.remove();
  }

  render() {
    if (this.state.showErrorPopup) {
      return <OktaError error={this.state.error} />;
    }
    return (
      <div className="outer-container">
        <div className="okta-widget" />
      </div>
    );
  }
}

export default withRouter(OktaSignInWidget);
